import moment from "moment";

import { VitalSignPatientData } from "@/Common/Models/VitalSignPatientData";
import { BloodGlucoseUnitsLookup, Height, SerumCreatinineUnitHardCoded, TemperatureUnitsLookup, Weight } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { HeightUnitConverter } from "@/Common/Utilities/Units/HeightUnitConverter";
import { BloodGlucoseConcentrationUnit, HeightUnit, SerumCreatinineConcentrationUnit, TemperatureUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { WeightUnitConverter } from "@/Common/Utilities/Units/WeightUnitConverter";
import { MedicalCalculator } from "@/Patient/Helpers/MedicalCalculator";
import { BloodPressure } from "../MedicalSummary/BloodPressure";
import { MedicalItemVitalSign } from "../MedicalSummary/MedicalItemVitalSign";
import { HeartRateRythm, VitalSign } from "./VitalSign";

export class VitalSignModelConverter {
    public static convertToMedicalItem(vitalSign: VitalSign, medicalItem?: MedicalItemVitalSign, patientData?: VitalSignPatientData): MedicalItemVitalSign {
        const weight = this.convertWeightToMedicalItemVitalSignModel(vitalSign.weight, vitalSign.weightUnit);
        const height = this.convertHeightToMedicalItemVitalSignModel(vitalSign.height, vitalSign.heightUnit);
        const bloodGlucose = this.convertBloodGlucoseUnitsToMedicalItemVitalSignModel(vitalSign.bloodGlucose, vitalSign.bloodGlucoseUnits);
        const headCircumference = this.convertHeightToMedicalItemVitalSignModel(vitalSign.headCircumference, vitalSign.headCircumferenceUnits);
        const waistCircumference = this.convertHeightToMedicalItemVitalSignModel(vitalSign.waistCircumference, vitalSign.waistCircumferenceUnits);
        const temperature = this.convertTemperatureUnitsToMedicalItemVitalSignModel(vitalSign.temperature, vitalSign.temperatureUnit);
        const serumCreatinineUnit = this.convertSerumCreatinineUnitsToMedicalItemVitalSignModel(vitalSign.serumCreatinine, vitalSign.serumCreatinineConcentrationUnit);

        return new MedicalItemVitalSign({
            ...medicalItem,
            Weight: weight.value,
            WeightUnitLookupId: weight.unitLookupId,
            PharmaStartDate: vitalSign.date,
            Height: height.value,
            HeightUnitLookupId: height.unitLookupId,
            BodySurface: vitalSign.bodySurface,
            MassBodyIndex: vitalSign.bodyMassIndex,
            BPSys: vitalSign.bloodPressure?.systolic,
            BPDia: vitalSign.bloodPressure?.diastolic,
            SerumCreatinine: vitalSign.serumCreatinine,
            CreatinineClearance: MedicalCalculator.calculateCreatinineClearance(patientData?.gender, patientData?.dateOfBirth, vitalSign),
            CreatinineUnit: serumCreatinineUnit.unitString,
            HeartRate: vitalSign.heartRate,
            O2Sat: vitalSign.oxygenSaturation,
            HeadCircumference: headCircumference.value,
            WaistCircumference: waistCircumference.value,
            RespiratoryRate: vitalSign.respirationRate,
            BPDiaStanding: vitalSign?.bloodPressureStanding?.diastolic,
            IsRegular: vitalSign.heartRateRythm !== HeartRateRythm.NoneSelected
                ? vitalSign.heartRateRythm === HeartRateRythm.Regular
                : null,
            BPSysStanding: vitalSign?.bloodPressureStanding?.systolic,
            BPDiaLaid: vitalSign?.bloodPressureLying?.diastolic,
            BPSysLaid: vitalSign?.bloodPressureLying?.systolic,
            Temperature: temperature.value,
            BloodGlucose: bloodGlucose.value,
            CreatinineClearanceAlgorythmLookupId: vitalSign.creatineClearanceAlgorithmLookupId,
            HeadCircumferenceUnitLookupId: headCircumference.unitLookupId,
            WaistCircumferenceUnitLookupId: waistCircumference.unitLookupId,
            TemperatureUnitLookupId: temperature.unitLookupId,
            TemperatureTypeLookupId: vitalSign.temperatureLocationLookupId,
            BloodGlucoseUnitLookupId: bloodGlucose.unitLookupId,
            PainScaleValue: vitalSign.painScaleValue,
            Note: vitalSign.note
        });
    }

    public static convertWeightToMedicalItemVitalSignModel(weight: number, unit: WeightUnit): { value: number; unitLookupId: string; } {
        if (IsNullOrUndefined(unit))
            return { value: weight, unitLookupId: null };

        let returnedWeight = weight;
        let unitLookupId: string = null;

        switch (unit) {
            case WeightUnit.Gram:
                returnedWeight = WeightUnitConverter.convert(unit, WeightUnit.Kilogram, weight);
            case WeightUnit.Kilogram:
                unitLookupId = cacheStore.FindLookupByLookupCode(Weight.Kilogram, Weight.Code)?.LookupID;
                break;
            case WeightUnit.Pound:
                unitLookupId = cacheStore.FindLookupByLookupCode(Weight.Pound, Weight.Code)?.LookupID;
                break;
        }

        return { value: returnedWeight, unitLookupId };
    }

    public static convertHeightToMedicalItemVitalSignModel(height: number, unit: HeightUnit): { value: number; unitLookupId: string; } {
        if (IsNullOrUndefined(unit))
            return { value: height, unitLookupId: null };

        let returnedWeight = height;
        let unitLookupId: string = null;

        switch (unit) {
            case HeightUnit.Meter:
                returnedWeight = HeightUnitConverter.convert(unit, HeightUnit.Centimeter, height);
            case HeightUnit.Centimeter:
                unitLookupId = cacheStore.FindLookupByLookupCode(Height.Centimeter, Height.Code)?.LookupID;
                break;
            case HeightUnit.Inch:
                unitLookupId = cacheStore.FindLookupByLookupCode(Height.Inch, Height.Code)?.LookupID;
                break;
        }

        return { value: returnedWeight, unitLookupId };
    }

    public static convertBloodGlucoseUnitsToMedicalItemVitalSignModel(concentration: number, unit: BloodGlucoseConcentrationUnit): { value: number; unitLookupId: string; } {
        if (IsNullOrUndefined(unit))
            return { value: concentration, unitLookupId: null };

        let returnedWeight = concentration;
        let unitLookupId: string = null;

        switch (unit) {
            case BloodGlucoseConcentrationUnit.millimolesPerLitre:
                unitLookupId = cacheStore.FindLookupByLookupCode(BloodGlucoseUnitsLookup.millimolesPerLitres, BloodGlucoseUnitsLookup.Code)?.LookupID;
                break;
            case BloodGlucoseConcentrationUnit.milligramPerDecilitre:
                unitLookupId = cacheStore.FindLookupByLookupCode(BloodGlucoseUnitsLookup.milligramsPerDecilitres, BloodGlucoseUnitsLookup.Code)?.LookupID;
                break;
        }

        return { value: returnedWeight, unitLookupId };
    }

    public static convertSerumCreatinineUnitsToMedicalItemVitalSignModel(concentration: number, unit: SerumCreatinineConcentrationUnit): { value: number; unitString: string; } {
        if (IsNullOrUndefined(unit))
            return { value: concentration, unitString: null };

        let returnedWeight = concentration;
        let unitString: string = null;

        switch (unit) {
            case SerumCreatinineConcentrationUnit.micromolesPerLitre:
                unitString = SerumCreatinineUnitHardCoded.MicromolesPerLitre.toString();
                break;
            case SerumCreatinineConcentrationUnit.milligramsPer100millilitre:
                unitString = SerumCreatinineUnitHardCoded.MilligramPer100Millilitres.toString();
                break;
        }

        return { value: returnedWeight, unitString };
    }

    public static convertTemperatureUnitsToMedicalItemVitalSignModel(temperature: number, unit: TemperatureUnit): { value: number; unitLookupId: string; } {
        if (IsNullOrUndefined(unit))
            return { value: temperature, unitLookupId: null };

        let returnedWeight = temperature;
        let unitLookupId: string = null;

        switch (unit) {
            case TemperatureUnit.Celsius:
                unitLookupId = cacheStore.FindLookupByLookupCode(TemperatureUnitsLookup.Celcius, TemperatureUnitsLookup.Code)?.LookupID;
                break;
            case TemperatureUnit.Fahrenheit:
                unitLookupId = cacheStore.FindLookupByLookupCode(TemperatureUnitsLookup.FahrenheitUnit, TemperatureUnitsLookup.Code)?.LookupID;
                break;
        }

        return { value: returnedWeight, unitLookupId };
    }

    public static convertToVitalSign(vitalSignMedicalItem: MedicalItemVitalSign): VitalSign {
        const vs = new MedicalItemVitalSign(vitalSignMedicalItem);

        return new VitalSign({
            weight: vs.Weight,
            weightUnit: vs.WeightUnit,
            date: vs.PharmaStartDate ? moment(vs.PharmaStartDate).toDate() : null,
            height: vs.Height,
            heightUnit: vs.HeightUnit,
            bodySurface: vs.BodySurface,
            bodyMassIndex: vs.MassBodyIndex,
            bloodPressure: new BloodPressure({ systolic: vs.BPSys, diastolic: vs.BPDia }),
            serumCreatinine: vs.SerumCreatinine,
            serumCreatinineConcentrationUnit: vs.SerumCreatinineUnit,
            heartRate: vs.HeartRate,
            oxygenSaturation: vs.O2Sat,
            headCircumference: vs.HeadCircumference,
            waistCircumference: vs.WaistCircumference,
            respirationRate: vs.RespiratoryRate,
            heartRateRythm: vs.IsRegular === true
                ? HeartRateRythm.Regular
                : vs.IsRegular === false
                    ? HeartRateRythm.Irregular
                    : HeartRateRythm.NoneSelected,
            bloodPressureStanding: new BloodPressure({ systolic: vs.BPSysStanding, diastolic: vs.BPDiaStanding }),
            bloodPressureLying: new BloodPressure({ systolic: vs.BPSysLaid, diastolic: vs.BPDiaLaid }),
            temperature: vs.Temperature,
            bloodGlucose: vs.BloodGlucose,
            creatineClearanceAlgorithmLookupId: vs.CreatinineClearanceAlgorythmLookupId,
            headCircumferenceUnits: vs.HeadCircumferenceUnit,
            waistCircumferenceUnits: vs.WaistCircumferenceUnit,
            temperatureUnit: vs.TemperatureUnit,
            temperatureLocationLookupId: vs.TemperatureTypeLookupId,
            bloodGlucoseUnits: vs.BloodGlucoseUnit,
            painScaleValue: vs.PainScaleValue,
            note: vs.Note,
            isCommited: vs.IsCommitted
        });
    }
}
